<template>
  <View direction="column" gap="md">
    <Text variant="h4">Screening confirmations</Text>

    <View direction="column" gap="lg">
      <Hint v-if="onFileLines.length" type="accent" icon="file-check-02">
        <View direction="column" gap="sm">
          <Text variant="h5">{{ onFileTitle }}</Text>
          <Text variant="body-medium">{{ onFileText }}</Text>
        </View>
      </Hint>

      <HintCollapsible
        v-if="coverage.outcome === 'covered' && requirements.attestations.length"
        icon="file-check-02"
        label="Review the attestations on file"
        :initially-expanded="false"
      >
        <View
          v-for="attestation in requirements.attestations"
          :key="attestation.attestationId"
          direction="column"
          gap="xs"
        >
          <Text variant="label-medium">
            {{ attestation.name }}
            <span v-if="!attestation.affirmativeRequired">— {{ answerOnFile(attestation.attestationId) }}</span>
          </Text>
          <Text variant="body-small" color="secondary">{{ attestation.legalText }}</Text>
        </View>
      </HintCollapsible>

      <Hint v-if="askAgainExplanation" icon="info-circle">
        {{ askAgainExplanation }}
      </Hint>

      <!--
        Order: the two providers' terms, TransUnion's property attestations, then the certification — each only
        when it is not on file for this person and property.
      -->
      <View v-if="asksRentPrepTerms" gap="sm" direction="row" align="center">
        <Checkbox id="label-agree-to-rent-prep-terms" v-model="isRentPrepTermsAccepted" />
        <Text variant="body-medium" as="label" for="label-agree-to-rent-prep-terms">
          <span>I accept&nbsp;</span>
          <Link :to="rentPrepTermsLink" color="accent" target="_blank">RentPrep's Terms & Conditions</Link>
        </Text>
      </View>

      <View v-if="asksTransUnionTerms" gap="sm" direction="row" align="center">
        <Checkbox id="label-agree-to-tu-terms" v-model="isTransUnionTermsAgreed" />
        <Text variant="body-medium" as="label" for="label-agree-to-tu-terms">
          <span>I agree to&nbsp;</span>
          <Link :to="transUnionTermsOfUseLink" color="accent" target="_blank">TransUnion Terms of Use</Link>
        </Text>
      </View>

      <!--
        TransUnion's attestation framework: affirmativeRequired means a statement the owner
        must affirm (checkbox), otherwise it is a yes/no question about the property whose
        answer TransUnion needs either way, so it gets radios and no default.
      -->
      <template v-if="asksAttestations">
        <View
          v-for="attestation in requirements.attestations"
          :key="attestation.attestationId"
          direction="column"
          gap="sm"
        >
          <template v-if="attestation.affirmativeRequired">
            <View gap="sm" direction="row" align="center">
              <Checkbox
                :id="`attestation-${attestation.attestationId}`"
                v-model="attestationAnswers[attestation.attestationId]"
              />
              <Text variant="body-medium" :as="'label'" :for="`attestation-${attestation.attestationId}`">
                {{ attestation.name }}
              </Text>
            </View>

            <HintCollapsible icon="file-check-02" label="View legal text" :initially-expanded="false">
              <Text variant="body-medium">{{ attestation.legalText }}</Text>
              <Text v-if="attestation.additionalInformation" variant="body-small" color="secondary">
                {{ attestation.additionalInformation }}
              </Text>
            </HintCollapsible>
          </template>

          <template v-else>
            <Text variant="label-medium">{{ attestation.name }}</Text>
            <Text variant="body-medium">{{ attestation.legalText }}</Text>
            <Text v-if="attestation.additionalInformation" variant="body-small" color="secondary">
              {{ attestation.additionalInformation }}
            </Text>

            <RadioButtonGroup direction="row">
              <RadioButton
                v-model="attestationAnswers[attestation.attestationId]"
                :name="`attestation-${attestation.attestationId}`"
                :value="true"
              >
                Yes
              </RadioButton>
              <RadioButton
                v-model="attestationAnswers[attestation.attestationId]"
                :name="`attestation-${attestation.attestationId}`"
                :value="false"
              >
                No
              </RadioButton>
            </RadioButtonGroup>
          </template>
        </View>
      </template>

      <View v-if="asksCertification" gap="sm" direction="row" align="start">
        <Checkbox id="label-certify-permissible-purpose" v-model="isPermissiblePurposeCertified" />
        <!-- The backend renders the wording, so what the owner reads is exactly what it records. -->
        <Text variant="body-medium" as="label" for="label-certify-permissible-purpose">
          {{ requirements.permissiblePurposeCertificationText }}
        </Text>
      </View>
    </View>
  </View>
</template>

<script setup lang="ts">
import { format } from 'date-fns';
import { computed, ref, watch } from 'vue';

import { BackgroundRecordsProvider } from '@tallo/applications';
import { Checkbox, Hint, HintCollapsible, Link, RadioButton, RadioButtonGroup, Text, View } from '@tallo/design-system';
import { rentPrepTermsLink, transUnionTermsOfUseLink } from '@tallo/navigation';

import { PropertyAttestationsDto } from '../interfaces/property-attestations.interface';
import { SendApplicationRequestDto } from '../interfaces/send-application-request.interface';

/**
 * The screening confirmations a send needs from the owner, asked only when not on file for this person and
 * property: the backend's requirements say what is covered (and by whom, until when), this form shows the rest.
 * `isComplete` gates the send; `toRequestBody()` is only what was shown, so whatever is covered gets replayed.
 */
const props = defineProps<{
  requirements: PropertyAttestationsDto;
}>();

const isTransUnionTermsAgreed = ref(false);
const isRentPrepTermsAccepted = ref(false);
const isPermissiblePurposeCertified = ref(false);
const attestationAnswers = ref<Record<number, boolean | undefined>>({});

const coverage = computed(() => props.requirements.attestationCoverage);
const certification = computed(() => props.requirements.permissiblePurposeCertification);
const isRentPrepScreened = computed(
  () => props.requirements.backgroundRecordsProvider === BackgroundRecordsProvider.RENT_PREP,
);

const asksAttestations = computed(
  () => coverage.value.outcome === 'confirmation-required' && props.requirements.attestations.length > 0,
);
const asksTransUnionTerms = computed(() => !props.requirements.transUnionTermsAccepted);
const asksRentPrepTerms = computed(() => isRentPrepScreened.value && !props.requirements.rentPrepTermsAccepted);
const asksCertification = computed(() => isRentPrepScreened.value && !certification.value);
/** With everything on file the form is the "on file" line alone, and the caller's button need not say "confirm". */
const asksAnything = computed(
  () => asksTransUnionTerms.value || asksRentPrepTerms.value || asksCertification.value || asksAttestations.value,
);

/**
 * When the owner is asked again after their answers lapsed, their previous answers to the yes/no questions about
 * the property are the starting point. Statements they must affirm start unticked: re-confirming them is the point.
 */
const prefilledAnswers = computed(() => {
  if (!asksAttestations.value) return [];

  const factIds = new Set(
    props.requirements.attestations
      .filter(({ affirmativeRequired }) => !affirmativeRequired)
      .map(({ attestationId }) => attestationId),
  );

  return (coverage.value.previousAnswers ?? []).filter(({ attestationId }) => factIds.has(attestationId));
});

// One form instance serves every send from its page, so answers must not carry over from the previous renter.
watch(
  () => props.requirements,
  () => {
    attestationAnswers.value = Object.fromEntries(
      prefilledAnswers.value.map(({ attestationId, isAffirmative }) => [attestationId, isAffirmative]),
    );
    isTransUnionTermsAgreed.value = false;
    isRentPrepTermsAccepted.value = false;
    isPermissiblePurposeCertified.value = false;
  },
  { immediate: true },
);

const formatDate = (value: string) => format(new Date(value), 'MMM d, yyyy');

const onFileLines = computed(() => {
  const lines: string[] = [];

  if (coverage.value.outcome === 'covered' && coverage.value.confirmedAt) {
    const by = coverage.value.confirmedBy ? ` by ${coverage.value.confirmedBy}` : '';
    lines.push(`TransUnion attestations confirmed ${formatDate(coverage.value.confirmedAt)}${by}`);
  }

  if (isRentPrepScreened.value && certification.value) {
    lines.push(`your FCRA certification from ${formatDate(certification.value.certifiedAt)}`);
  }

  const acceptedTerms = [
    isRentPrepScreened.value && props.requirements.rentPrepTermsAccepted ? 'RentPrep' : null,
    props.requirements.transUnionTermsAccepted ? 'TransUnion' : null,
  ].filter(Boolean);

  if (acceptedTerms.length) {
    lines.push(`${acceptedTerms.join(' and ')} terms accepted`);
  }

  return lines;
});

/** One sentence: whichever item leads (the certification does when the attestations lapsed) starts capitalized. */
const onFileText = computed(() => {
  const text = onFileLines.value.join(' · ');

  return text.charAt(0).toUpperCase() + text.slice(1);
});

/** Only the certification expires; the attestations stand until TransUnion changes them, the terms for good. */
const onFileUntil = computed(() => (isRentPrepScreened.value ? certification.value?.expiresAt : undefined));

const onFileTitle = computed(() =>
  onFileUntil.value ? `On file for this property until ${formatDate(onFileUntil.value)}` : 'On file for this property',
);

/** Why something on file is asked again, in one place: a first-time ask needs no explanation. */
const askAgainExplanation = computed(() => {
  const reasons: string[] = [];

  if (asksAttestations.value && coverage.value.reason === 'requirements-changed') {
    reasons.push('TransUnion changed what it asks for this property.');
  }

  if (asksCertification.value && props.requirements.permissiblePurposeCertificationReason === 'certification-expired') {
    reasons.push('Your FCRA certification for this property is older than 90 days.');
  }

  if (asksCertification.value && props.requirements.permissiblePurposeCertificationReason === 'wording-changed') {
    reasons.push('The wording of the FCRA certification changed since you last certified.');
  }

  if (!reasons.length) return undefined;

  reasons.push(
    prefilledAnswers.value.length
      ? 'Please confirm again below; your previous answers about the property are filled in.'
      : 'Please confirm again below.',
  );

  return reasons.join(' ');
});

function answerOnFile(attestationId: number) {
  const answer = coverage.value.previousAnswers?.find((response) => response.attestationId === attestationId);

  return answer ? (answer.isAffirmative ? 'Yes' : 'No') : '';
}

const allAttestationsAnswered = computed(() =>
  props.requirements.attestations.every(({ attestationId, affirmativeRequired }) => {
    const answer = attestationAnswers.value[attestationId];

    return affirmativeRequired ? answer === true : typeof answer === 'boolean';
  }),
);

const isComplete = computed(
  () =>
    (!asksTransUnionTerms.value || isTransUnionTermsAgreed.value) &&
    (!asksRentPrepTerms.value || isRentPrepTermsAccepted.value) &&
    (!asksCertification.value || isPermissiblePurposeCertified.value) &&
    (!asksAttestations.value || allAttestationsAnswered.value),
);

/** Only what was shown: the backend replays whatever is on file. */
function toRequestBody(): SendApplicationRequestDto {
  const body: SendApplicationRequestDto = {};

  if (asksTransUnionTerms.value) {
    body.acceptTransUnionTerms = isTransUnionTermsAgreed.value;
  }

  if (asksAttestations.value) {
    // The send stays disabled until every attestation has an explicit answer, so an unanswered one here is a bug, not a "no".
    body.attestation = {
      attestationGroupId: props.requirements.attestationGroupId,
      attestationResponses: props.requirements.attestations.map(({ attestationId }) => ({
        attestationId,
        isAffirmative: attestationAnswers.value[attestationId] === true,
      })),
    };
  }

  if (asksRentPrepTerms.value) {
    body.acceptRentPrepTerms = isRentPrepTermsAccepted.value;
  }

  if (asksCertification.value) {
    body.certifyPermissiblePurpose = isPermissiblePurposeCertified.value;
  }

  return body;
}

defineExpose({ isComplete, asksAnything, toRequestBody });
</script>
